/**
 * 앱을 열자마자 쓸 수 있는 지난번 로그인 사본과 책 목록 사본.
 *
 * Firebase 로그인은 앱을 열 때마다 서버에 두 번 묻고 나서야(만료된 토큰 갱신 + 계정 확인)
 * 누가 로그인했는지 알려 준다(@firebase/auth 의 reloadAndSetCurrentUserOrClear).
 * Firestore 도 그때까지는 기기에 저장해 둔 데이터조차 내주지 않는다. 그래서 공유를 눌러
 * 들어오면 이 왕복이 끝날 때까지 'LOADING' 화면만 보였다.
 *
 * 공유받은 문장 화면은 받은 글을 기기에서 꺼내므로 로그인 확인을 기다릴 이유가 없다.
 * 지난번에 로그인해 있었으면 이 사본으로 먼저 그리고, 책 고르기는 책 목록 사본으로 한다.
 * 확인 결과 로그인이 풀렸으면 로그인 화면으로 바뀐다(초안은 남는다).
 * 로그아웃하면 두 사본을 모두 지운다 — 같은 기기를 다른 사람이 쓸 수 있다.
 */

const SESSION_KEY = 'br_session';
const BOOKS_KEY = 'br_book_snapshot';

// 공유 화면이 책을 고르고 쪽수를 환산하는 데 필요한 것만 남긴다
const BOOK_FIELDS = ['id', 'title', 'author', 'status', 'totalPages', 'ebookTotalPages', 'pageMode'];

export const readSessionHint = () => {
  try {
    const hint = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    return hint && typeof hint.uid === 'string' && hint.uid ? hint : null;
  } catch {
    return null;
  }
};

/** 로그인이 확인된 사용자를 적어 둔다. 온보딩을 마치지 않았으면 적지 않는다 */
export const writeSessionHint = (user) => {
  try {
    if (user?.uid && user.isOnboardingCompleted === true) {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        uid: user.uid,
        email: user.email || '',
        photoURL: user.photoURL || '',
        isOnboardingCompleted: true
      }));
    } else {
      localStorage.removeItem(SESSION_KEY);
    }
  } catch { /* 저장 실패는 무시 */ }
};

/** 로그아웃 — 로그인 사본과 책 목록 사본을 함께 지운다 */
export const clearSessionCache = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(BOOKS_KEY);
  } catch { /* 무시 */ }
};

/**
 * 로그인 확인이 끝나기 전에 앱을 그릴 사용자.
 * 확인이 끝났으면 그 결과를 그대로 쓰고, 아직이면 공유받은 문장 화면으로 열렸고
 * 지난번에 로그인해 있었을 때만 사본을 쓴다. 그 밖에는 null (로딩 화면).
 */
export const gateUser = ({ user, loading, hint, path }) => {
  if (user || !loading) return user || null;
  if (path !== '/share' || hint?.isOnboardingCompleted !== true) return null;
  return { ...hint, provisional: true };
};

export const toBookSnapshot = (books = []) => books.map(book =>
  Object.fromEntries(BOOK_FIELDS.filter(field => book[field] !== undefined).map(field => [field, book[field]]))
);

/** 책 목록 사본을 남긴다. 바뀐 게 없으면 쓰지 않는다 (책 스냅숏은 자주 온다) */
export const saveBookSnapshot = (books) => {
  try {
    const next = JSON.stringify(toBookSnapshot(books));
    if (localStorage.getItem(BOOKS_KEY) !== next) localStorage.setItem(BOOKS_KEY, next);
  } catch { /* 저장 실패는 무시 */ }
};

export const loadBookSnapshot = () => {
  try {
    const books = JSON.parse(localStorage.getItem(BOOKS_KEY) || '[]');
    return Array.isArray(books) ? books.filter(book => book && typeof book.id === 'string') : [];
  } catch {
    return [];
  }
};
